// Pure rank helpers for the work drag kernel (W1 Track A).
//
// Everything here is unit-tier logic lifted OUT of the controller so it can be
// tested without a DOM (app/javascript/lib/work/__tests__/work_rank_helpers.test.ts)
// — R4's "drag logic that is testable IS tested". The one deliberate exception
// is `directionOf`: the RTL lookup seam reads the live computed style, because
// that is the only way to know how the host laid the surface out (gap-fill 4).
//
// Rank contract (anti-idea 3): neighbour deltas only. Every helper answers
// { before_id, after_id } — never an absolute order array, never index math.
// Neighbours are computed against the container's current order with the moved
// ids REMOVED, so a slot never names the dragged block itself.

// Vertical containers (board columns, backlog groups) use top/bottom edges;
// horizontal ones (chip rows, timeline lanes) use left/right. The axis is
// surface-supplied via the container's `data-axis` attribute.
export const AXES = ["vertical", "horizontal"]

// Read the writing direction of `element` (or its nearest styled ancestor).
// Defaults to "ltr" — including in test environments where getComputedStyle
// answers with an empty string.
export function directionOf(element) {
  if (!element || typeof element !== "object") return "ltr"
  let direction
  try {
    direction = window.getComputedStyle(element).direction
  } catch {
    return "ltr"
  }
  return direction === "rtl" ? "rtl" : "ltr"
}

// Map a closest-edge (p-dnd hitbox) to an insert side relative to the edge's
// item. Vertical edges are direction-independent; horizontal ones flip under
// RTL because the visual left of an RTL row is its logical end (gap-fill 4).
export function mapEdgeToInsert({ edge, direction = "ltr", axis = "vertical" }) {
  if (!edge) return null
  if (axis === "horizontal") {
    if (edge === "left") return direction === "rtl" ? "after" : "before"
    if (edge === "right") return direction === "rtl" ? "before" : "after"
    return null
  }
  if (edge === "top") return "before"
  if (edge === "bottom") return "after"
  return null
}

// The insert slot for a drop on ITEM `targetId` with the given closest edge.
//
// - `order` is the container's current item-id order (pre-move DOM order).
// - `movedIds` keeps its internal order in the caller's payload (the kernel
//   passes it through verbatim); the slot computation only cares that they are
//   excluded from the neighbour walk.
// - Returns `{ before_id, after_id }` where either side may be `null` (the
//   container's edges) — or `null` when the drop makes no sense (no edge, or
//   the target id is not in the order).
export function neighbourSlot({ order, movedIds, targetId, edge, direction = "ltr", axis = "vertical" }) {
  const insert = mapEdgeToInsert({ edge, direction, axis })
  if (!insert || !targetId) return null

  const moved = movedIds || []
  const rest = order.filter((id) => !moved.includes(id))
  const index = rest.indexOf(targetId)
  if (index === -1) return null

  if (insert === "before") {
    return { before_id: rest[index - 1] ?? null, after_id: rest[index] ?? null }
  }
  return { before_id: rest[index] ?? null, after_id: rest[index + 1] ?? null }
}

// The insert slot for a drop on a CONTAINER's blank area: append at the end.
// An empty container (or one whose only cards were all moved) yields
// `{ before_id: null, after_id: null }` — the neighbour-delta way of saying
// "empty slot" (pin 17).
export function appendSlot({ order, movedIds }) {
  const moved = movedIds || []
  const rest = order.filter((id) => !moved.includes(id))
  return { before_id: rest.length ? rest[rest.length - 1] : null, after_id: null }
}
